import { normalizeCoordinatePair } from './socialMapCoordinate';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';

export const SOCIAL_MAP_DATA_SOURCE = 'SOCIAL_DISCOVERY';
export const SOCIAL_MAP_LOCATION_AUTHORITY = 'GEO_LOCATION_NODE';
export const SOCIAL_MAP_POST_COORDINATE_STORAGE = 'NONE';
export const SOCIAL_MAP_MARKER_TYPES = 'FOOD,PLACE';
export const SOCIAL_MAP_NO_MAPPOINT = true;
export const MAP_RENDERER = 'GOOGLE_MAPS_JS';
export const MAP_DOMAIN_COORDINATE = 'WGS84';
export const MAP_PROVIDER_IDS_IN_DOMAIN = false;
export const OSMF_PUBLIC_FALLBACK = false;
export const MAP_MISSING_CONFIG_FALLBACK = 'PROVIDER_NEUTRAL_SURFACE';
export const SOCIAL_MAP_MARKER_KINDS = Object.freeze(['FOOD', 'PLACE']);
export const SOCIAL_MAP_MARKER_IDENTITY = 'postId';
export const SOCIAL_MAP_OVERLAP_POLICY = 'GROUP_BY_LOCATION_ID_FOR_PRESENTATION_ONLY';
export const SOCIAL_MAP_GROUP_IDENTITY = 'linkedLocation.locationId';
export const SOCIAL_MAP_DISCOVERY_LIMIT = 20;

/**
 * 地點群組內貼文排序：兩種排序都由 backend 對「單一聚焦地點」決定順序，前端不計分、不重排。
 * RELEVANCE＝sort=RELEVANCE＋locationId（backend 權威相關度）。
 * LATEST＝sort=LATEST＋locationId（backend 時間序）。
 * 未聚焦的群組只是 discovery 預覽，不代表已依任一排序排列。
 */
export const SOCIAL_MAP_GROUP_SORTS = Object.freeze([
  Object.freeze({ id: 'RELEVANCE', label: '最相關' }),
  Object.freeze({ id: 'LATEST', label: '最新' }),
]);
export const SOCIAL_MAP_DEFAULT_GROUP_SORT = 'RELEVANCE';
/** 群組 key 即 linkedLocation.locationId，故只查該節點本身，不含子地點。 */
export const SOCIAL_MAP_FOCUSED_LOCATION_SCOPE = 'EXACT';

function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

function textPreview(text) {
  const value = String(text || '').trim();
  if (value.length <= 120) return value;
  return `${value.slice(0, 117)}...`;
}

function markerLabel(kind, locationName, count) {
  if (count > 1) return `${locationName} 的 ${count} 則貼文`;
  if (kind === 'FOOD') return `${locationName} 的美食貼文`;
  return `${locationName} 的地點貼文`;
}

export function toSocialMapMarker(item) {
  const kind = String(item?.kind || '');
  if (!SOCIAL_MAP_MARKER_KINDS.includes(kind)) return null;
  const postId = snowflakeId(item?.postId);
  if (!postId) return null;
  const location = item?.location;
  if (!location || location.available === false) return null;
  const pair = normalizeCoordinatePair(location);
  if (!pair.markerAvailable) return null;
  const locationId = snowflakeId(location.locationId);
  if (!locationId) return null;
  const locationName = String(location.displayName || 'Named place').trim() || 'Named place';
  const author = item?.author || {};
  return {
    postId,
    postKind: kind,
    latitude: pair.latitude,
    longitude: pair.longitude,
    locationId,
    locationName,
    author: {
      memberId: snowflakeId(author.memberId),
      displayName: travelMemberProductDisplayName({
        displayName: author.displayName,
        username: author.username,
        nickname: author.nickname,
      }),
      avatarUrl: author.avatarUrl || '',
    },
    coverFileReference: item.coverFileReference || '',
    coverUrl: item.coverUrl || '',
    textPreview: textPreview(item.text),
    publishedAt: item.publishedAt || null,
    likeCount: Number.isFinite(item?.engagement?.likeCount) ? item.engagement.likeCount : 0,
    commentCount: Number.isFinite(item?.engagement?.commentCount) ? item.engagement.commentCount : 0,
    item,
  };
}

export function buildSocialMapMarkers(items) {
  const markers = [];
  const skipped = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const kind = String(item?.kind || '');
    if (!SOCIAL_MAP_MARKER_KINDS.includes(kind)) {
      skipped.push({ reason: 'kind', postId: snowflakeId(item?.postId) });
      return;
    }
    const marker = toSocialMapMarker(item);
    if (marker) markers.push(marker);
    else skipped.push({ reason: 'ineligible', postId: snowflakeId(item?.postId) });
  });
  return { markers, skipped };
}

/** 以穩定的 linkedLocation.locationId 分組；群組順序沿用 discovery 首次出現順序。 */
export function groupMarkersByLocation(markers) {
  const groups = [];
  const indexByKey = new Map();
  (Array.isArray(markers) ? markers : []).forEach((marker) => {
    const key = marker.locationId;
    if (!key) return;
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({
        key,
        locationId: marker.locationId,
        latitude: marker.latitude,
        longitude: marker.longitude,
        locationName: marker.locationName,
        markers: [],
      });
    }
    groups[indexByKey.get(key)].markers.push(marker);
  });
  return groups.map((group) => ({
    ...group,
    label: markerLabel(
      group.markers[0]?.postKind,
      group.locationName,
      group.markers.length,
    ),
  }));
}

export function isSupportedGroupSort(sortId) {
  return SOCIAL_MAP_GROUP_SORTS.some((option) => option.id === sortId);
}

export function groupSortLabel(sortId) {
  return SOCIAL_MAP_GROUP_SORTS.find((option) => option.id === sortId)?.label || '';
}

/** 聚焦地點的 discovery 查詢；cursor 不在此組裝，backend 回傳的 cursor 一律原樣保留。 */
export function buildFocusedLocationQuery({ kind = 'ALL', timeRange = 'ALL', locationId, sort }) {
  return {
    kind: kind || 'ALL',
    timeRange: timeRange || 'ALL',
    locationId,
    locationScope: SOCIAL_MAP_FOCUSED_LOCATION_SCOPE,
    sort,
    limit: SOCIAL_MAP_DISCOVERY_LIMIT,
  };
}

/** 只保留屬於該地點且可上地圖的貼文；維持 backend 回傳順序，不重排。 */
export function focusedLocationMarkers(items, locationId) {
  return buildSocialMapMarkers(items).markers.filter((marker) => marker.locationId === locationId);
}

export function projectMarkerGroups(groups) {
  const list = Array.isArray(groups) ? groups : [];
  if (!list.length) return [];
  const lats = list.map((group) => group.latitude);
  const lngs = list.map((group) => group.longitude);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  return list.map((group) => {
    const x = lngSpan === 0 ? 50 : 8 + ((group.longitude - minLng) / lngSpan) * 84;
    const y = latSpan === 0 ? 50 : 8 + ((maxLat - group.latitude) / latSpan) * 84;
    return { ...group, x, y };
  });
}

export function socialMapEmptyKind({ items, markers, hasLocationFilter }) {
  if (!Array.isArray(items) || items.length === 0) return 'no-posts';
  if (!markers.length && hasLocationFilter) return 'no-markers-filtered';
  if (!markers.length) return 'no-markers';
  return null;
}
